import { motion } from "framer-motion";
import { WifiOff, Lock, HardDrive, ServerOff } from "lucide-react";

export default function OfflineViz() {
  return (
    <div className="relative w-[70%] md:w-full max-w-[520px] mx-auto aspect-square">
      {/* subtle grid */}
      <div className="absolute inset-0 rounded-[32px] bp-grid opacity-50" aria-hidden />

      <svg viewBox="0 0 400 400" className="relative w-full h-full">
        {/* Broken server / offline symbol on the left */}
        <g transform="translate(20 130)" opacity="0.7">
          <rect x="0" y="0" width="72" height="20" rx="4" fill="#1E293B" stroke="rgba(255,255,255,0.08)" />
          <rect x="0" y="28" width="72" height="20" rx="4" fill="#1E293B" stroke="rgba(255,255,255,0.08)" />
          <rect x="0" y="56" width="72" height="20" rx="4" fill="#1E293B" stroke="rgba(255,255,255,0.08)" />
          {/* X mark */}
          <g transform="translate(86 34)" stroke="#00FAF8" strokeWidth="2" strokeLinecap="round">
            <line x1="0" y1="0" x2="16" y2="16" />
            <line x1="16" y1="0" x2="0" y2="16" />
          </g>
        </g>

        {/* Central iPhone (top-down silhouette) */}
        <g transform="translate(160 60)">
          <motion.g
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          >
            <rect
              x="0"
              y="0"
              width="140"
              height="280"
              rx="26"
              fill="#0F172A"
              stroke="rgba(0,250,248,0.4)"
              strokeWidth="1.4"
            />
            <rect
              x="6"
              y="6"
              width="128"
              height="268"
              rx="20"
              fill="url(#offGrad)"
              stroke="rgba(255,255,255,0.05)"
            />
            <defs>
              <linearGradient id="offGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#0e1a30" />
                <stop offset="1" stopColor="#060b18" />
              </linearGradient>
            </defs>
            {/* screen content */}
            <rect x="18" y="20" width="60" height="6" rx="3" fill="rgba(0,250,248,0.6)" />
            <rect x="18" y="34" width="90" height="4" rx="2" fill="rgba(255,255,255,0.2)" />
            <rect x="18" y="44" width="70" height="4" rx="2" fill="rgba(255,255,255,0.14)" />

            {/* Lock icon */}
            <g transform="translate(50 90)">
              <rect x="0" y="14" width="40" height="30" rx="6" fill="rgba(0,250,248,0.15)" stroke="rgba(0,250,248,0.5)" />
              <path d="M8 14 V8 C8 -4 32 -4 32 8 V14" fill="none" stroke="rgba(0,250,248,0.6)" strokeWidth="1.5" />
              <circle cx="20" cy="26" r="3" fill="#00FAF8" />
            </g>

            {/* Data lines */}
            <rect x="18" y="160" width="60" height="4" rx="2" fill="rgba(255,255,255,0.12)" />
            <rect x="18" y="170" width="90" height="4" rx="2" fill="rgba(255,255,255,0.1)" />
            <rect x="18" y="180" width="72" height="4" rx="2" fill="rgba(255,255,255,0.1)" />
            <rect x="18" y="200" width="106" height="30" rx="6" fill="rgba(0,250,248,0.06)" stroke="rgba(0,250,248,0.2)" />
            <text
              x="30"
              y="220"
              fill="rgba(0,250,248,0.9)"
              fontFamily="JetBrains Mono, monospace"
              fontSize="10"
            >
              verschlüsselt
            </text>
          </motion.g>
        </g>

        {/* Broken connection line */}
        <g stroke="rgba(0,250,248,0.5)" strokeWidth="1.4" strokeDasharray="4 6" fill="none">
          <path d="M92 168 L156 200" />
        </g>
        <g transform="translate(120 170)">
          <circle r="9" fill="#0F172A" stroke="rgba(0,250,248,0.4)" />
          <line x1="-4" y1="-4" x2="4" y2="4" stroke="#00FAF8" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="4" y1="-4" x2="-4" y2="4" stroke="#00FAF8" strokeWidth="1.5" strokeLinecap="round" />
        </g>
      </svg>

      {/* Chips */}
      <FloatingChip icon={<WifiOff size={12} className="text-[#00FAF8]" />} label="keine dauerhafte Verbindung nötig" pos="top-8 right-4" delay={0.2} />
      <FloatingChip icon={<HardDrive size={12} className="text-[#00FAF8]" />} label="Falldaten lokal" pos="bottom-16 right-2" delay={0.4} />
      <FloatingChip icon={<Lock size={12} className="text-[#00FAF8]" />} label="Biometrie oder Passwort" pos="bottom-4 left-6" delay={0.6} />
      <FloatingChip icon={<ServerOff size={12} className="text-[#00FAF8]" />} label="nach Anmeldung offline" pos="top-2 left-2" delay={0.8} />
    </div>
  );
}

function FloatingChip({ icon, label, pos, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute ${pos} flex items-center gap-2 rounded-full border border-white/10 bg-[#0F172A]/85 backdrop-blur px-3 py-1.5`}
    >
      {icon}
      <span className="text-[11px] font-mono-bp text-white/85 whitespace-nowrap">
        {label}
      </span>
    </motion.div>
  );
}
